import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Shield, FileText, BarChart3, AlertTriangle, MessageSquare, Activity, ClipboardCheck, CreditCard, Mail, Database, Award, ChevronLeft, Check, Clock, XCircle, Plus, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

function StatusBadge({ status }) {
  const colors = { COMPLIANT: 'bg-forest text-white', PENDING: 'bg-gold text-navy', ACTION_REQUIRED: 'bg-red-500 text-white', OPEN: 'bg-red-500 text-white', CLOSED: 'bg-forest text-white', IN_PROGRESS: 'bg-gold text-navy' };
  return <Badge className={`${colors[status] || 'bg-gray-200 text-gray-700'} text-xs font-body`} data-testid={`status-badge-${status}`}>{status?.replace('_', ' ')}</Badge>;
}

function RiskBadge({ level }) {
  const colors = { LOW: 'bg-forest/20 text-forest', MEDIUM: 'bg-gold/20 text-gold', HIGH: 'bg-red-100 text-red-700' };
  return <Badge className={`${colors[level] || ''} font-body`}>{level}</Badge>;
}

function SectionCard({ title, children, icon: Icon }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h3 className="font-heading font-bold text-navy text-sm mb-4 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-gold" />} {title}</h3>
      {children}
    </div>
  );
}

export default function CompliancePanel() {
  const { user } = useAuth();
  const [complianceStatus, setComplianceStatus] = useState([]);
  const [regDocs, setRegDocs] = useState([]);
  const [auditPackage, setAuditPackage] = useState(null);
  const [transparencyReport, setTransparencyReport] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [riskMonitoring, setRiskMonitoring] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [processorDefense, setProcessorDefense] = useState(null);
  const [regComms, setRegComms] = useState([]);
  const [backupStatus, setBackupStatus] = useState(null);
  const [certifications, setCertifications] = useState([]);

  // Forms
  const [newDoc, setNewDoc] = useState({ name: '', category: 'Corporate', description: '', file_url: '', status: 'PENDING' });
  const [newComplaint, setNewComplaint] = useState({ date: '', category: 'member_complaint', description: '', resolution: '', responsible_staff: '', status: 'OPEN' });
  const [newReview, setNewReview] = useState({ review_year: new Date().getFullYear(), areas_reviewed: [], findings: '', recommendations: '', reviewer: '', status: 'IN_PROGRESS' });
  const [newComm, setNewComm] = useState({ date: '', agency: '', comm_type: 'inquiry', subject: '', description: '', deadline: '', status: 'PENDING', approved_by: '' });
  const [newCert, setNewCert] = useState({ certification_date: '', responsible_officer: 'Chuck Clark', governance_controls: false, financial_controls: false, membership_controls: false, payment_safeguards: false, recordkeeping_policies: false, regulatory_readiness: false, review_signature: '' });
  const [editingStatus, setEditingStatus] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, d, a, t, c, r, rv, p, rc, b, ct] = await Promise.all([
        axios.get(`${API}/admin/compliance/status`, cfg),
        axios.get(`${API}/admin/compliance/documents`, cfg),
        axios.get(`${API}/admin/compliance/audit-package`, cfg),
        axios.get(`${API}/admin/compliance/transparency-report`, cfg),
        axios.get(`${API}/admin/compliance/complaints`, cfg),
        axios.get(`${API}/admin/compliance/risk-monitoring`, cfg),
        axios.get(`${API}/admin/compliance/reviews`, cfg),
        axios.get(`${API}/admin/compliance/processor-defense`, cfg),
        axios.get(`${API}/admin/compliance/communications`, cfg),
        axios.get(`${API}/admin/compliance/backup-status`, cfg),
        axios.get(`${API}/admin/compliance/certifications`, cfg),
      ]);
      setComplianceStatus(s.data); setRegDocs(d.data); setAuditPackage(a.data); setTransparencyReport(t.data);
      setComplaints(c.data); setRiskMonitoring(r.data); setReviews(rv.data); setProcessorDefense(p.data);
      setRegComms(rc.data); setBackupStatus(b.data); setCertifications(ct.data);
    } catch { toast.error('Failed to load compliance data'); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const updateStatus = async (itemId) => {
    if (!editingStatus) return;
    try {
      await axios.put(`${API}/admin/compliance/status/${itemId}`, editingStatus, cfg);
      toast.success('Status updated'); setEditingStatus(null); loadAll();
    } catch { toast.error('Failed'); }
  };

  const addDoc = async () => {
    if (!newDoc.name) return;
    try {
      await axios.post(`${API}/admin/compliance/documents`, newDoc, cfg);
      toast.success('Document added'); setNewDoc({ name: '', category: 'Corporate', description: '', file_url: '', status: 'PENDING' }); loadAll();
    } catch { toast.error('Failed'); }
  };

  const addComplaint = async () => {
    if (!newComplaint.description || !newComplaint.date) return;
    try {
      await axios.post(`${API}/admin/compliance/complaints`, newComplaint, cfg);
      toast.success('Incident logged'); setNewComplaint({ date: '', category: 'member_complaint', description: '', resolution: '', responsible_staff: '', status: 'OPEN' }); loadAll();
    } catch { toast.error('Failed'); }
  };

  const addReview = async () => {
    if (!newReview.reviewer) return;
    try {
      await axios.post(`${API}/admin/compliance/reviews`, newReview, cfg);
      toast.success('Review created'); loadAll();
    } catch { toast.error('Failed'); }
  };

  const addComm = async () => {
    if (!newComm.subject || !newComm.date) return;
    try {
      await axios.post(`${API}/admin/compliance/communications`, newComm, cfg);
      toast.success('Communication logged'); setNewComm({ date: '', agency: '', comm_type: 'inquiry', subject: '', description: '', deadline: '', status: 'PENDING', approved_by: '' }); loadAll();
    } catch { toast.error('Failed'); }
  };

  const addCert = async () => {
    if (!newCert.certification_date || !newCert.review_signature) return;
    try {
      await axios.post(`${API}/admin/compliance/certifications`, newCert, cfg);
      toast.success('Certification recorded'); loadAll();
    } catch { toast.error('Failed'); }
  };

  const compliantCount = complianceStatus.filter(i => i.status === 'COMPLIANT').length;
  const pendingCount = complianceStatus.filter(i => i.status === 'PENDING').length;
  const actionCount = complianceStatus.filter(i => i.status === 'ACTION_REQUIRED').length;

  return (
    <div className="pt-16 min-h-screen bg-[#FAFAFA]" data-testid="compliance-panel">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin" className="text-sm text-navy font-body hover:text-gold mb-1 inline-flex items-center gap-1" data-testid="back-to-admin"><ChevronLeft className="w-3 h-3" /> Admin Panel</Link>
            <h1 className="text-2xl font-heading font-bold text-navy">Compliance & Audit Readiness</h1>
            <p className="text-sm text-gray-600 font-body">FIREPWR / Safe Florida 501(c)(4) Regulatory Framework</p>
          </div>
          <div className="flex gap-2">
            <Badge className="bg-forest/20 text-forest font-body">{compliantCount} Compliant</Badge>
            <Badge className="bg-gold/20 text-gold font-body">{pendingCount} Pending</Badge>
            {actionCount > 0 && <Badge className="bg-red-100 text-red-700 font-body">{actionCount} Action Required</Badge>}
          </div>
        </div>

        <Tabs defaultValue="status" className="space-y-4">
          <TabsList className="bg-white border border-gray-200 rounded-lg p-1 flex-wrap h-auto gap-1" data-testid="compliance-tabs">
            <TabsTrigger value="status" className="font-body text-xs" data-testid="comp-tab-status"><Shield className="w-3 h-3 mr-1" />Status</TabsTrigger>
            <TabsTrigger value="vault" className="font-body text-xs" data-testid="comp-tab-vault"><FileText className="w-3 h-3 mr-1" />Vault</TabsTrigger>
            <TabsTrigger value="audit" className="font-body text-xs" data-testid="comp-tab-audit"><ClipboardCheck className="w-3 h-3 mr-1" />Audit</TabsTrigger>
            <TabsTrigger value="transparency" className="font-body text-xs" data-testid="comp-tab-transparency"><BarChart3 className="w-3 h-3 mr-1" />Transparency</TabsTrigger>
            <TabsTrigger value="complaints" className="font-body text-xs" data-testid="comp-tab-complaints"><MessageSquare className="w-3 h-3 mr-1" />Incidents</TabsTrigger>
            <TabsTrigger value="risk" className="font-body text-xs" data-testid="comp-tab-risk"><Activity className="w-3 h-3 mr-1" />Risk</TabsTrigger>
            <TabsTrigger value="reviews" className="font-body text-xs" data-testid="comp-tab-reviews"><ClipboardCheck className="w-3 h-3 mr-1" />Reviews</TabsTrigger>
            <TabsTrigger value="processor" className="font-body text-xs" data-testid="comp-tab-processor"><CreditCard className="w-3 h-3 mr-1" />Processor</TabsTrigger>
            <TabsTrigger value="communications" className="font-body text-xs" data-testid="comp-tab-comms"><Mail className="w-3 h-3 mr-1" />Comms</TabsTrigger>
            <TabsTrigger value="backup" className="font-body text-xs" data-testid="comp-tab-backup"><Database className="w-3 h-3 mr-1" />Backup</TabsTrigger>
            <TabsTrigger value="certification" className="font-body text-xs" data-testid="comp-tab-cert"><Award className="w-3 h-3 mr-1" />Certification</TabsTrigger>
          </TabsList>

          {/* SECTION 1: COMPLIANCE STATUS */}
          <TabsContent value="status" data-testid="compliance-status-section">
            <SectionCard title="COMPLIANCE STATUS DASHBOARD" icon={Shield}>
              <div className="space-y-3">
                {complianceStatus.map(item => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4" data-testid={`compliance-item-${item.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-body font-bold text-navy text-sm">{item.name}</h4>
                          <StatusBadge status={item.status} />
                        </div>
                        <p className="text-xs text-gray-600 font-body">{item.description}</p>
                        {item.notes && <p className="text-xs text-gray-500 font-body mt-1 italic">{item.notes}</p>}
                        <p className="text-xs text-gray-400 font-body mt-1">Officer: {item.responsible_officer} | Updated: {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A'}</p>
                      </div>
                      <Button variant="outline" size="sm" className="font-body text-xs ml-3" onClick={() => setEditingStatus(editingStatus?.id === item.id ? null : { id: item.id, status: item.status, notes: item.notes || '', responsible_officer: item.responsible_officer || '' })} data-testid={`edit-status-${item.id}`}>Edit</Button>
                    </div>
                    {editingStatus?.id === item.id && (
                      <div className="mt-3 pt-3 border-t border-gray-100 grid sm:grid-cols-3 gap-2">
                        <select value={editingStatus.status} onChange={e => setEditingStatus(p => ({ ...p, status: e.target.value }))} className="border rounded-md p-2 text-xs font-body">
                          <option value="COMPLIANT">COMPLIANT</option><option value="PENDING">PENDING</option><option value="ACTION_REQUIRED">ACTION REQUIRED</option>
                        </select>
                        <Input value={editingStatus.notes} onChange={e => setEditingStatus(p => ({ ...p, notes: e.target.value }))} placeholder="Notes" className="text-xs font-body" />
                        <div className="flex gap-2">
                          <Input value={editingStatus.responsible_officer} onChange={e => setEditingStatus(p => ({ ...p, responsible_officer: e.target.value }))} placeholder="Officer" className="text-xs font-body" />
                          <Button onClick={() => updateStatus(item.id)} className="btn-primary-safe font-body text-xs" data-testid={`save-status-${item.id}`}>Save</Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>
          </TabsContent>

          {/* SECTION 2: REGULATORY DOCUMENT VAULT */}
          <TabsContent value="vault" data-testid="regulatory-vault-section">
            <SectionCard title="REGULATORY DOCUMENT VAULT" icon={FileText}>
              <div className="mb-6 p-4 bg-navy/5 rounded-lg">
                <h4 className="font-body font-bold text-navy text-xs mb-3">Add Document</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  <Input value={newDoc.name} onChange={e => setNewDoc(p => ({ ...p, name: e.target.value }))} placeholder="Document name" className="text-xs font-body" data-testid="vault-doc-name" />
                  <select value={newDoc.category} onChange={e => setNewDoc(p => ({ ...p, category: e.target.value }))} className="border rounded-md p-2 text-xs font-body">
                    {['Corporate', 'Federal', 'State', 'Governance', 'Financial', 'Operational', 'Compliance'].map(c => <option key={c}>{c}</option>)}
                  </select>
                  <Input value={newDoc.file_url} onChange={e => setNewDoc(p => ({ ...p, file_url: e.target.value }))} placeholder="File URL" className="text-xs font-body" />
                  <Button onClick={addDoc} className="btn-primary-safe font-body text-xs" data-testid="vault-add-doc-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
                </div>
                <Input value={newDoc.description} onChange={e => setNewDoc(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="text-xs font-body mt-2" />
              </div>
              <div className="space-y-2">
                {regDocs.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-3" data-testid={`vault-doc-${doc.id}`}>
                    <div className="flex items-center gap-3 flex-1">
                      <FileText className="w-4 h-4 text-navy flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-body font-medium text-navy truncate">{doc.name}</p>
                        <p className="text-xs text-gray-500 font-body">{doc.category} | {doc.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <StatusBadge status={doc.status} />
                      {doc.file_url && <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-gold font-body hover:underline"><Download className="w-3 h-3" /></a>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 font-body mt-4">All documents are securely stored, access-controlled, and time-stamped. {regDocs.length} documents in vault.</p>
            </SectionCard>
          </TabsContent>

          {/* SECTION 3: AUDIT RESPONSE PACKAGE */}
          <TabsContent value="audit" data-testid="audit-package-section">
            <SectionCard title="AUDIT RESPONSE PACKAGE" icon={ClipboardCheck}>
              {auditPackage && (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-body font-bold text-navy text-xs mb-2">ORGANIZATIONAL OVERVIEW</h4>
                      <p className="text-xs font-body text-gray-700">{auditPackage.organization?.name}</p>
                      <p className="text-xs font-body text-gray-600">{auditPackage.organization?.type}</p>
                      <p className="text-xs font-body text-gray-600">{auditPackage.organization?.location}</p>
                      <p className="text-xs font-body text-gray-600">Leader: {auditPackage.organization?.leader}</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-body font-bold text-navy text-xs mb-2">MISSION STATEMENT</h4>
                      <p className="text-xs font-body text-gray-700">{auditPackage.mission}</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-body font-bold text-navy text-xs mb-2">GOVERNANCE STRUCTURE</h4>
                      <p className="text-xs font-body text-gray-700">{auditPackage.governance?.structure}</p>
                      {auditPackage.governance?.officers?.map((o, i) => <p key={i} className="text-xs font-body text-gray-600">{o}</p>)}
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-body font-bold text-navy text-xs mb-2">FINANCIAL SUMMARY</h4>
                      <p className="text-xs font-body text-gray-700">Total Revenue: ${auditPackage.financial_summary?.total_revenue?.toLocaleString()}</p>
                      <p className="text-xs font-body text-gray-600">Membership Revenue: ${auditPackage.financial_summary?.total_membership_revenue?.toLocaleString()}</p>
                      <p className="text-xs font-body text-gray-600">Store Revenue: ${auditPackage.financial_summary?.total_store_revenue?.toLocaleString()}</p>
                      <p className="text-xs font-body text-gray-600">Members: {auditPackage.financial_summary?.total_members} (Active: {auditPackage.financial_summary?.active_members})</p>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-body font-bold text-navy text-xs mb-2">MEMBERSHIP POLICIES</h4>
                    <div className="grid sm:grid-cols-4 gap-2">
                      <p className="text-xs font-body text-gray-700">Fee: ${auditPackage.membership_policies?.monthly_fee}/mo</p>
                      <p className="text-xs font-body text-gray-700">Cycle: {auditPackage.membership_policies?.billing_cycle}</p>
                      <p className="text-xs font-body text-gray-700">Processor: {auditPackage.membership_policies?.payment_processor}</p>
                      <p className="text-xs font-body text-gray-700">Refund: {auditPackage.membership_policies?.refund_policy}</p>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-body font-bold text-navy text-xs mb-2">OPERATIONAL PROCEDURES</h4>
                    <ul className="space-y-1">{auditPackage.operational_procedures?.map((p, i) => <li key={i} className="text-xs font-body text-gray-700 flex items-center gap-2"><Check className="w-3 h-3 text-forest" />{p}</li>)}</ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-body font-bold text-navy text-xs mb-2">COMPLIANCE STATUS SUMMARY</h4>
                    <div className="grid sm:grid-cols-3 gap-2">
                      {auditPackage.compliance_status?.map(s => (
                        <div key={s.id} className="flex items-center gap-2"><StatusBadge status={s.status} /><span className="text-xs font-body text-gray-700">{s.name}</span></div>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 font-body">Generated: {new Date(auditPackage.generated_at).toLocaleString()} | Exportable as single PDF</p>
                </div>
              )}
            </SectionCard>
          </TabsContent>

          {/* SECTION 4: FINANCIAL TRANSPARENCY REPORT */}
          <TabsContent value="transparency" data-testid="transparency-report-section">
            <SectionCard title="ANNUAL TRANSPARENCY REPORT" icon={BarChart3}>
              {transparencyReport && (
                <div className="space-y-4">
                  <h4 className="font-heading font-bold text-navy">Fiscal Year {transparencyReport.current_year}</h4>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {[
                      { label: 'Total Membership Revenue', value: `$${transparencyReport.total_membership_revenue?.toLocaleString()}` },
                      { label: 'Total Store Revenue', value: `$${transparencyReport.total_store_revenue?.toLocaleString()}` },
                      { label: 'Total Revenue', value: `$${transparencyReport.total_revenue?.toLocaleString()}` },
                      { label: 'Program Expenses', value: `$${transparencyReport.program_expenses?.toLocaleString()}` },
                      { label: 'Administrative Expenses', value: `$${transparencyReport.administrative_expenses?.toLocaleString()}` },
                      { label: 'Reserve Balance', value: `$${transparencyReport.reserve_balance?.toLocaleString()}` },
                    ].map(({ label, value }) => (
                      <div key={label} className="border border-gray-200 rounded-lg p-4 text-center">
                        <p className="text-xs text-gray-500 font-body uppercase tracking-wide">{label}</p>
                        <p className="text-xl font-heading font-bold text-navy">{value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 font-body">This report is generated annually and reflects actual revenue data from the membership and store systems.</p>
                </div>
              )}
            </SectionCard>
          </TabsContent>

          {/* SECTION 5: COMPLAINT AND INCIDENT LOG */}
          <TabsContent value="complaints" data-testid="complaints-section">
            <SectionCard title="COMPLAINT AND INCIDENT LOG" icon={MessageSquare}>
              <div className="mb-6 p-4 bg-navy/5 rounded-lg">
                <h4 className="font-body font-bold text-navy text-xs mb-3">Log Incident</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <Input type="date" value={newComplaint.date} onChange={e => setNewComplaint(p => ({ ...p, date: e.target.value }))} className="text-xs font-body" data-testid="complaint-date" />
                  <select value={newComplaint.category} onChange={e => setNewComplaint(p => ({ ...p, category: e.target.value }))} className="border rounded-md p-2 text-xs font-body" data-testid="complaint-category">
                    {['member_complaint', 'refund_dispute', 'chargeback', 'security_incident', 'regulatory_inquiry', 'operational_error', 'payment_failure'].map(c => <option key={c} value={c}>{c.replace(/_/g, ' ').toUpperCase()}</option>)}
                  </select>
                  <Input value={newComplaint.responsible_staff} onChange={e => setNewComplaint(p => ({ ...p, responsible_staff: e.target.value }))} placeholder="Responsible staff" className="text-xs font-body" />
                </div>
                <Textarea value={newComplaint.description} onChange={e => setNewComplaint(p => ({ ...p, description: e.target.value }))} placeholder="Description of incident" rows={2} className="text-xs font-body mt-2" data-testid="complaint-description" />
                <div className="flex gap-2 mt-2">
                  <Input value={newComplaint.resolution} onChange={e => setNewComplaint(p => ({ ...p, resolution: e.target.value }))} placeholder="Resolution (if resolved)" className="text-xs font-body flex-1" />
                  <Button onClick={addComplaint} className="btn-primary-safe font-body text-xs" data-testid="log-complaint-btn">Log Incident</Button>
                </div>
              </div>
              <div className="space-y-2">
                {complaints.map(c => (
                  <div key={c.id} className="border border-gray-200 rounded-lg p-3" data-testid={`complaint-${c.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-navy/10 text-navy text-xs font-body">{c.category?.replace(/_/g, ' ')}</Badge>
                        <StatusBadge status={c.status} />
                      </div>
                      <span className="text-xs text-gray-400 font-body">{c.date}</span>
                    </div>
                    <p className="text-sm font-body text-gray-700">{c.description}</p>
                    {c.resolution && <p className="text-xs font-body text-forest mt-1">Resolution: {c.resolution}</p>}
                    <p className="text-xs text-gray-400 font-body mt-1">Staff: {c.responsible_staff || 'Unassigned'} | Logged by: {c.logged_by}</p>
                  </div>
                ))}
                {complaints.length === 0 && <p className="text-sm text-gray-500 font-body text-center py-4">No incidents logged</p>}
              </div>
            </SectionCard>
          </TabsContent>

          {/* SECTION 6: RISK MONITORING */}
          <TabsContent value="risk" data-testid="risk-monitoring-section">
            <SectionCard title="RISK MONITORING SYSTEM" icon={Activity}>
              {riskMonitoring && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-body font-bold text-sm text-navy">Overall Risk Level:</span>
                    <RiskBadge level={riskMonitoring.overall_risk} />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {[
                      { label: 'Chargeback Rate', value: `${riskMonitoring.metrics?.chargeback_rate}%`, threshold: `Max: ${riskMonitoring.thresholds?.chargeback_rate_max}%`, warn: riskMonitoring.metrics?.chargeback_rate > riskMonitoring.thresholds?.chargeback_rate_max },
                      { label: 'Cancellation Rate', value: `${riskMonitoring.metrics?.cancellation_rate}%`, threshold: `Max: ${riskMonitoring.thresholds?.cancellation_rate_max}%`, warn: riskMonitoring.metrics?.cancellation_rate > riskMonitoring.thresholds?.cancellation_rate_max },
                      { label: 'Monthly Complaints', value: riskMonitoring.metrics?.complaints_this_month, threshold: `Max: ${riskMonitoring.thresholds?.monthly_complaint_max}`, warn: riskMonitoring.metrics?.complaints_this_month > riskMonitoring.thresholds?.monthly_complaint_max },
                      { label: 'Payment Failures', value: riskMonitoring.metrics?.payment_failure_count, threshold: 'Monitor', warn: riskMonitoring.metrics?.payment_failure_count > 0 },
                      { label: 'Open Legal Inquiries', value: riskMonitoring.metrics?.open_legal_inquiries, threshold: 'Target: 0', warn: riskMonitoring.metrics?.open_legal_inquiries > 0 },
                      { label: 'Pending Reg. Comms', value: riskMonitoring.metrics?.pending_reg_communications, threshold: 'Target: 0', warn: riskMonitoring.metrics?.pending_reg_communications > 0 },
                    ].map(({ label, value, threshold, warn }) => (
                      <div key={label} className={`border rounded-lg p-4 ${warn ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                        <p className="text-xs text-gray-500 font-body uppercase tracking-wide">{label}</p>
                        <p className={`text-xl font-heading font-bold ${warn ? 'text-red-600' : 'text-navy'}`}>{String(value)}</p>
                        <p className="text-xs text-gray-400 font-body">{threshold}</p>
                      </div>
                    ))}
                  </div>
                  {riskMonitoring.alerts?.length > 0 && (
                    <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                      <h4 className="font-body font-bold text-red-700 text-xs mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> AUTOMATED ALERTS</h4>
                      {riskMonitoring.alerts.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs font-body text-red-700 py-1">
                          <RiskBadge level={a.level} /> {a.message}
                        </div>
                      ))}
                    </div>
                  )}
                  {riskMonitoring.alerts?.length === 0 && <p className="text-sm text-forest font-body flex items-center gap-2"><Check className="w-4 h-4" /> All metrics within acceptable thresholds</p>}
                </div>
              )}
            </SectionCard>
          </TabsContent>

          {/* SECTION 7: ANNUAL COMPLIANCE REVIEW */}
          <TabsContent value="reviews" data-testid="compliance-reviews-section">
            <SectionCard title="ANNUAL COMPLIANCE REVIEW" icon={ClipboardCheck}>
              <div className="mb-6 p-4 bg-navy/5 rounded-lg">
                <h4 className="font-body font-bold text-navy text-xs mb-3">Create Review</h4>
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input type="number" value={newReview.review_year} onChange={e => setNewReview(p => ({ ...p, review_year: parseInt(e.target.value) }))} className="text-xs font-body" />
                  <Input value={newReview.reviewer} onChange={e => setNewReview(p => ({ ...p, reviewer: e.target.value }))} placeholder="Reviewer name" className="text-xs font-body" data-testid="review-reviewer" />
                </div>
                <p className="text-xs text-gray-500 font-body mt-2 mb-1">Areas to review:</p>
                <div className="flex flex-wrap gap-2">
                  {['Financial Records', 'Membership Records', 'Policies & Procedures', 'Regulatory Filings', 'Governance Documents', 'Security Controls'].map(area => (
                    <label key={area} className="flex items-center gap-1 text-xs font-body text-gray-700">
                      <input type="checkbox" checked={newReview.areas_reviewed.includes(area)} onChange={e => { if (e.target.checked) setNewReview(p => ({ ...p, areas_reviewed: [...p.areas_reviewed, area] })); else setNewReview(p => ({ ...p, areas_reviewed: p.areas_reviewed.filter(a => a !== area) })); }} className="rounded" /> {area}
                    </label>
                  ))}
                </div>
                <Textarea value={newReview.findings} onChange={e => setNewReview(p => ({ ...p, findings: e.target.value }))} placeholder="Findings" rows={2} className="text-xs font-body mt-2" />
                <Textarea value={newReview.recommendations} onChange={e => setNewReview(p => ({ ...p, recommendations: e.target.value }))} placeholder="Recommendations" rows={2} className="text-xs font-body mt-2" />
                <Button onClick={addReview} className="btn-primary-safe font-body text-xs mt-2" data-testid="create-review-btn">Create Review</Button>
              </div>
              <div className="space-y-3">
                {reviews.map(r => (
                  <div key={r.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-body font-bold text-navy text-sm">{r.review_year} Annual Review</h4>
                      <StatusBadge status={r.status} />
                    </div>
                    <p className="text-xs text-gray-600 font-body">Reviewer: {r.reviewer}</p>
                    <p className="text-xs text-gray-600 font-body">Areas: {r.areas_reviewed?.join(', ')}</p>
                    {r.findings && <p className="text-xs text-gray-700 font-body mt-1">Findings: {r.findings}</p>}
                    {r.recommendations && <p className="text-xs text-forest font-body mt-1">Recommendations: {r.recommendations}</p>}
                  </div>
                ))}
                {reviews.length === 0 && <p className="text-sm text-gray-500 font-body text-center py-4">No compliance reviews recorded</p>}
              </div>
            </SectionCard>
          </TabsContent>

          {/* SECTION 8: PAYMENT PROCESSOR DEFENSE */}
          <TabsContent value="processor" data-testid="processor-defense-section">
            <SectionCard title="PAYMENT PROCESSOR DEFENSE FILE" icon={CreditCard}>
              {processorDefense && (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-body font-bold text-navy text-xs mb-2">Terms of Service</h4>
                      <p className="text-xs font-body text-gray-700">URL: {processorDefense.terms_of_service?.url}</p>
                      <p className="text-xs font-body text-gray-600">Last Updated: {processorDefense.terms_of_service?.last_updated}</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-body font-bold text-navy text-xs mb-2">Refund Policy</h4>
                      <p className="text-xs font-body text-gray-700">{processorDefense.refund_policy?.description}</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-body font-bold text-navy text-xs mb-2">Membership Agreement</h4>
                      <p className="text-xs font-body text-gray-700">Fee: ${processorDefense.membership_agreement?.monthly_fee}/mo</p>
                      <p className="text-xs font-body text-gray-600">Billing: {processorDefense.membership_agreement?.billing_cycle}</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-body font-bold text-navy text-xs mb-2">Billing Authorization</h4>
                      <p className="text-xs font-body text-gray-700">Method: {processorDefense.billing_authorization?.method}</p>
                      <p className="text-xs font-body text-gray-600">Type: {processorDefense.billing_authorization?.authorization_type}</p>
                      <p className="text-xs font-body text-gray-600">Sandbox: {processorDefense.billing_authorization?.sandbox_mode ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-body font-bold text-navy text-xs mb-2">Transaction Summary</h4>
                    <div className="grid sm:grid-cols-4 gap-3">
                      <div><p className="text-xs text-gray-500 font-body">Store Transactions</p><p className="text-lg font-heading font-bold text-navy">{processorDefense.transaction_summary?.total_store_transactions}</p></div>
                      <div><p className="text-xs text-gray-500 font-body">Membership Payments</p><p className="text-lg font-heading font-bold text-navy">{processorDefense.transaction_summary?.total_membership_payments}</p></div>
                      <div><p className="text-xs text-gray-500 font-body">Chargebacks</p><p className="text-lg font-heading font-bold text-navy">{processorDefense.transaction_summary?.total_chargebacks}</p></div>
                      <div><p className="text-xs text-gray-500 font-body">Chargeback Rate</p><p className="text-lg font-heading font-bold text-navy">{processorDefense.transaction_summary?.chargeback_rate}%</p></div>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-body font-bold text-navy text-xs mb-2">Fraud Prevention Procedures</h4>
                    <ul className="space-y-1">{processorDefense.fraud_prevention?.map((f, i) => <li key={i} className="text-xs font-body text-gray-700 flex items-center gap-2"><Check className="w-3 h-3 text-forest" />{f}</li>)}</ul>
                  </div>
                  <p className="text-xs text-gray-400 font-body">This file is ready for submission to payment processors upon request.</p>
                </div>
              )}
            </SectionCard>
          </TabsContent>

          {/* SECTION 9: REGULATORY COMMUNICATIONS */}
          <TabsContent value="communications" data-testid="regulatory-comms-section">
            <SectionCard title="REGULATORY RESPONSE PROTOCOL" icon={Mail}>
              <div className="mb-4 p-3 bg-gold/10 border border-gold/30 rounded-lg">
                <p className="text-xs font-body text-navy font-bold">Protocol Requirements:</p>
                <ul className="text-xs font-body text-gray-700 space-y-1 mt-1">
                  <li className="flex items-center gap-1"><Check className="w-3 h-3 text-forest" />All regulatory inquiries must be logged</li>
                  <li className="flex items-center gap-1"><Check className="w-3 h-3 text-forest" />Responses must be documented</li>
                  <li className="flex items-center gap-1"><Check className="w-3 h-3 text-forest" />Deadlines must be tracked</li>
                  <li className="flex items-center gap-1"><Check className="w-3 h-3 text-forest" />Official responses must be approved by leadership before submission</li>
                </ul>
              </div>
              <div className="mb-6 p-4 bg-navy/5 rounded-lg">
                <h4 className="font-body font-bold text-navy text-xs mb-3">Log Communication</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <Input type="date" value={newComm.date} onChange={e => setNewComm(p => ({ ...p, date: e.target.value }))} className="text-xs font-body" data-testid="comm-date" />
                  <Input value={newComm.agency} onChange={e => setNewComm(p => ({ ...p, agency: e.target.value }))} placeholder="Agency/Entity" className="text-xs font-body" data-testid="comm-agency" />
                  <select value={newComm.comm_type} onChange={e => setNewComm(p => ({ ...p, comm_type: e.target.value }))} className="border rounded-md p-2 text-xs font-body">
                    {['inquiry', 'response', 'filing', 'notice'].map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                  </select>
                  <Input value={newComm.subject} onChange={e => setNewComm(p => ({ ...p, subject: e.target.value }))} placeholder="Subject" className="text-xs font-body" data-testid="comm-subject" />
                  <Input type="date" value={newComm.deadline} onChange={e => setNewComm(p => ({ ...p, deadline: e.target.value }))} placeholder="Deadline" className="text-xs font-body" />
                  <Input value={newComm.approved_by} onChange={e => setNewComm(p => ({ ...p, approved_by: e.target.value }))} placeholder="Approved by" className="text-xs font-body" />
                </div>
                <Textarea value={newComm.description} onChange={e => setNewComm(p => ({ ...p, description: e.target.value }))} placeholder="Description" rows={2} className="text-xs font-body mt-2" />
                <Button onClick={addComm} className="btn-primary-safe font-body text-xs mt-2" data-testid="log-comm-btn">Log Communication</Button>
              </div>
              <div className="space-y-2">
                {regComms.map(c => (
                  <div key={c.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-navy/10 text-navy text-xs font-body">{c.comm_type}</Badge>
                        <StatusBadge status={c.status} />
                      </div>
                      <span className="text-xs text-gray-400 font-body">{c.date}</span>
                    </div>
                    <p className="text-sm font-body font-medium text-navy">{c.subject}</p>
                    <p className="text-xs font-body text-gray-600">Agency: {c.agency}</p>
                    {c.deadline && <p className="text-xs font-body text-red-600 flex items-center gap-1"><Clock className="w-3 h-3" />Deadline: {c.deadline}</p>}
                    {c.approved_by && <p className="text-xs font-body text-forest">Approved by: {c.approved_by}</p>}
                  </div>
                ))}
                {regComms.length === 0 && <p className="text-sm text-gray-500 font-body text-center py-4">No regulatory communications logged</p>}
              </div>
            </SectionCard>
          </TabsContent>

          {/* SECTION 10: BUSINESS CONTINUITY & BACKUP */}
          <TabsContent value="backup" data-testid="backup-section">
            <SectionCard title="CONTINUITY AND BACKUP" icon={Database}>
              {backupStatus && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-body font-bold text-sm text-navy">System Status:</span>
                    <Badge className="bg-forest text-white font-body">{backupStatus.status}</Badge>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-body font-bold text-navy text-xs mb-2">Backup Policy</h4>
                      <p className="text-xs font-body text-gray-700">Frequency: {backupStatus.backup_policy?.frequency}</p>
                      <p className="text-xs font-body text-gray-600">Storage: {backupStatus.backup_policy?.storage}</p>
                      <p className="text-xs font-body text-gray-600">Retention: {backupStatus.backup_policy?.retention}</p>
                      <p className="text-xs font-body text-gray-600">Encryption: {backupStatus.backup_policy?.encryption}</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-body font-bold text-navy text-xs mb-2">Database Status</h4>
                      <p className="text-xs font-body text-gray-700">Collections: {backupStatus.database?.total_collections}</p>
                      {backupStatus.database?.collection_stats && Object.entries(backupStatus.database.collection_stats).map(([k, v]) => (
                        <p key={k} className="text-xs font-body text-gray-600">{k}: {v} records</p>
                      ))}
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-body font-bold text-navy text-xs mb-2">Recovery Procedures</h4>
                    <ul className="space-y-1">{backupStatus.recovery_procedures?.map((p, i) => <li key={i} className="text-xs font-body text-gray-700 flex items-center gap-2"><Check className="w-3 h-3 text-forest" />{p}</li>)}</ul>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-body font-bold text-navy text-xs mb-2">Access Restoration</h4>
                    <ul className="space-y-1">{backupStatus.access_restoration?.map((p, i) => <li key={i} className="text-xs font-body text-gray-700 flex items-center gap-2"><Check className="w-3 h-3 text-forest" />{p}</li>)}</ul>
                  </div>
                  <p className="text-xs text-gray-400 font-body">Last backup: {new Date(backupStatus.last_backup).toLocaleString()}</p>
                </div>
              )}
            </SectionCard>
          </TabsContent>

          {/* SECTION 11: COMPLIANCE CERTIFICATION */}
          <TabsContent value="certification" data-testid="certification-section">
            <SectionCard title="COMPLIANCE CERTIFICATION" icon={Award}>
              <div className="mb-6 p-4 bg-navy/5 rounded-lg">
                <h4 className="font-body font-bold text-navy text-xs mb-3">Record Certification</h4>
                <div className="grid sm:grid-cols-2 gap-2 mb-3">
                  <div><Label className="font-body text-xs">Certification Date</Label><Input type="date" value={newCert.certification_date} onChange={e => setNewCert(p => ({ ...p, certification_date: e.target.value }))} className="mt-1 text-xs font-body" data-testid="cert-date" /></div>
                  <div><Label className="font-body text-xs">Responsible Officer</Label><Input value={newCert.responsible_officer} onChange={e => setNewCert(p => ({ ...p, responsible_officer: e.target.value }))} className="mt-1 text-xs font-body" data-testid="cert-officer" /></div>
                </div>
                <p className="text-xs font-body text-navy font-bold mb-2">Confirm implementation of:</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {[
                    { key: 'governance_controls', label: 'Governance Controls' },
                    { key: 'financial_controls', label: 'Financial Controls' },
                    { key: 'membership_controls', label: 'Membership Controls' },
                    { key: 'payment_safeguards', label: 'Payment System Safeguards' },
                    { key: 'recordkeeping_policies', label: 'Recordkeeping Policies' },
                    { key: 'regulatory_readiness', label: 'Regulatory Readiness Procedures' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 text-xs font-body text-gray-700">
                      <input type="checkbox" checked={newCert[key]} onChange={e => setNewCert(p => ({ ...p, [key]: e.target.checked }))} className="rounded" data-testid={`cert-${key}`} /> {label}
                    </label>
                  ))}
                </div>
                <div className="mt-3">
                  <Label className="font-body text-xs">Review Signature</Label>
                  <Input value={newCert.review_signature} onChange={e => setNewCert(p => ({ ...p, review_signature: e.target.value }))} placeholder="Type full name to sign" className="mt-1 text-xs font-body" data-testid="cert-signature" />
                </div>
                <Button onClick={addCert} className="btn-primary-safe font-body text-xs mt-3" data-testid="record-cert-btn">Record Certification</Button>
              </div>
              <div className="space-y-3">
                {certifications.map(c => (
                  <div key={c.id} className="border border-gray-200 rounded-lg p-4" data-testid={`cert-record-${c.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-body font-bold text-navy text-sm">Compliance Certification</h4>
                      <span className="text-xs text-gray-400 font-body">{c.certification_date}</span>
                    </div>
                    <p className="text-xs font-body text-gray-600">Officer: {c.responsible_officer}</p>
                    <div className="grid sm:grid-cols-3 gap-1 mt-2">
                      {[
                        { k: 'governance_controls', l: 'Governance' }, { k: 'financial_controls', l: 'Financial' },
                        { k: 'membership_controls', l: 'Membership' }, { k: 'payment_safeguards', l: 'Payments' },
                        { k: 'recordkeeping_policies', l: 'Records' }, { k: 'regulatory_readiness', l: 'Regulatory' },
                      ].map(({ k, l }) => (
                        <span key={k} className="text-xs font-body flex items-center gap-1">
                          {c[k] ? <Check className="w-3 h-3 text-forest" /> : <XCircle className="w-3 h-3 text-red-400" />}{l}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs font-body text-navy mt-2 font-signature text-lg">Signed: {c.review_signature}</p>
                    <p className="text-xs text-gray-400 font-body">Certified by: {c.certified_by} | {new Date(c.created_at).toLocaleString()}</p>
                  </div>
                ))}
                {certifications.length === 0 && <p className="text-sm text-gray-500 font-body text-center py-4">No certifications recorded yet</p>}
              </div>
            </SectionCard>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
